import {
  getAccountPageUrl,
  getContactPageUrl,
  getDocumentsPageUrl,
  getFilesPageUrl,
  getInvoicingPageUrl,
  getPeriodPageUrl,
  getSettingsPageUrl,
  getVATPageUrl,
  getWorkflowBuilderPageUrl
} from '@constants'
import {
  ChangeLogDiffOp,
  IChangeLogDiffSpan,
  IChangeLogFieldChange,
  IChangeLogResource,
  IChangeLogValueRef
} from '@query/changeLog'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowRight } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled, { DefaultTheme } from 'styled-components'
import { humanizeChangeLogValue } from './changeLogHumanize'

export const getChangeLogResourceUrl = (
  slug: string,
  resource: Pick<IChangeLogResource, 'type' | 'id'>
): string | null => {
  switch (resource.type) {
    case 'document':
      return getDocumentsPageUrl(slug, { id: resource.id })
    case 'invoice':
      return getInvoicingPageUrl(slug, { id: resource.id, type: 'sales' })
    case 'invoice_row':
      return null
    case 'purchase_invoice':
      return getInvoicingPageUrl(slug, { id: resource.id, type: 'purchase' })
    case 'attachment':
      return getFilesPageUrl(slug, { id: resource.id, idType: 'file' })
    case 'account':
      return getAccountPageUrl(slug, { id: resource.id })
    case 'contact':
      return getContactPageUrl(slug, { id: resource.id })
    case 'period':
      return getPeriodPageUrl(slug, { id: resource.id })
    case 'vat_period':
      return getVATPageUrl(slug, { id: resource.id })
    case 'business':
      return getSettingsPageUrl(slug)
    case 'business_usership':
      return getSettingsPageUrl(slug, { page: 'people' })
    case 'document_relation':
      return null
    case 'tag':
      return getSettingsPageUrl(slug, { page: 'tags' })
    case 'workflow':
      return getWorkflowBuilderPageUrl(slug, resource.id)
    default:
      return null
  }
}

const getDiffSpanTone = (op: ChangeLogDiffOp): 'old' | 'new' | 'equal' => {
  if (op === 'delete') return 'old'
  if (op === 'insert') return 'new'
  return 'equal'
}

const DiffSpans: React.FC<{ spans: IChangeLogDiffSpan[]; field?: string }> = ({ spans, field }) => {
  const [t] = useTranslation()
  return (
    <DiffText>
      {spans.map((span, index) => (
        <ValuePill key={`${span.op}-${index}`} $tone={getDiffSpanTone(span.op)}>
          {humanizeChangeLogValue(span.text, t, field)}
        </ValuePill>
      ))}
    </DiffText>
  )
}

const RefValue: React.FC<{
  slug: string
  valueRef: IChangeLogValueRef
  tone: 'old' | 'new'
  field?: string
}> = ({ slug, valueRef, tone, field }) => {
  const [t] = useTranslation()
  const label = humanizeChangeLogValue(valueRef.label, t, field) ?? valueRef.label
  const href = getChangeLogResourceUrl(slug, valueRef)

  if (!href) {
    return <ValuePill $tone={tone}>{label}</ValuePill>
  }

  return (
    <ValuePillLink to={href} $tone={tone}>
      {label}
    </ValuePillLink>
  )
}

const ArrowIcon = () => (
  <Arrow>
    <FaArrowRight />
  </Arrow>
)

const RefChangeLine: React.FC<{
  fieldLabel: string
  field: string
  slug: string
  oldRefs: IChangeLogValueRef[]
  newRefs: IChangeLogValueRef[]
}> = ({ fieldLabel, field, slug, oldRefs, newRefs }) => (
  <ChangeLine>
    <FieldLabel>{fieldLabel}:</FieldLabel>{' '}
    {oldRefs.map(ref => (
      <RefValue
        key={`old-${ref.type}-${ref.id}`}
        slug={slug}
        valueRef={ref}
        tone="old"
        field={field}
      />
    ))}
    {oldRefs.length > 0 && newRefs.length > 0 ? <ArrowIcon /> : null}
    {newRefs.map(ref => (
      <RefValue
        key={`new-${ref.type}-${ref.id}`}
        slug={slug}
        valueRef={ref}
        tone="new"
        field={field}
      />
    ))}
  </ChangeLine>
)

const getBlueprintDisplayTone = (display: string, change: IChangeLogFieldChange): 'old' | 'new' => {
  if (display.startsWith('+ ')) return 'new'
  if (display.startsWith('− ') || display.startsWith('- ')) return 'old'
  if (change.new != null && change.new !== '') return 'new'
  return 'old'
}

const BlueprintFieldChange: React.FC<{
  change: IChangeLogFieldChange
  slug: string
}> = ({ change, slug }) => {
  const [t] = useTranslation()
  const refs = change.refs ?? []
  const accountRef = refs.find(ref => ref.type === 'account')
  const display = humanizeChangeLogValue(change.display, t, change.field) ?? change.display

  if (!display || !accountRef) {
    return (
      <ChangeLine>
        <FieldLabel>{change.field_label}:</FieldLabel> {display}
      </ChangeLine>
    )
  }

  const tone = getBlueprintDisplayTone(display, change)
  const prefixMatch = display.match(/^([+−-] )/)
  const prefix = prefixMatch?.[1] ?? ''
  const body = prefix ? display.slice(prefix.length) : display
  const accountLabel = humanizeChangeLogValue(accountRef.label, t, change.field) ?? accountRef.label
  const accountIndex = body.indexOf(accountLabel)

  // If the account label isn't found in the display body, render the display
  // text as-is — don't also inject a RefValue (that duplicates the account name).
  if (accountIndex === -1) {
    return (
      <ChangeLine>
        <FieldLabel>{change.field_label}:</FieldLabel>{' '}
        <ValuePill $tone={tone}>
          {prefix}
          {body}
        </ValuePill>
      </ChangeLine>
    )
  }

  const before = body.slice(0, accountIndex)
  const after = body.slice(accountIndex + accountLabel.length)

  return (
    <ChangeLine>
      <FieldLabel>{change.field_label}:</FieldLabel>{' '}
      <ValuePill $tone={tone}>
        {prefix}
        {before}
        <RefValue slug={slug} valueRef={accountRef} tone={tone} field={change.field} />
        {after}
      </ValuePill>
    </ChangeLine>
  )
}

export const FieldChange: React.FC<{ change: IChangeLogFieldChange; slug: string }> = ({
  change,
  slug
}) => {
  const [t] = useTranslation()
  const refs = change.refs ?? []
  const oldRefs = refs.filter(ref => ref.side === 'old')
  const newRefs = refs.filter(ref => ref.side === 'new')
  const isBlueprint = change.field.startsWith('blueprint.')
  const hasRefs = oldRefs.length > 0 || newRefs.length > 0

  // Prefer RefChangeLine whenever refs are present, including blueprint.* fields
  // that aren't account-id display lines (e.g. amount/vat changes with account refs).
  if (hasRefs) {
    return (
      <RefChangeLine
        fieldLabel={change.field_label}
        field={change.field}
        slug={slug}
        oldRefs={oldRefs}
        newRefs={newRefs}
      />
    )
  }

  if (isBlueprint) {
    return <BlueprintFieldChange change={change} slug={slug} />
  }

  if (change.diff && change.diff.length > 0) {
    return (
      <ChangeLine>
        <FieldLabel>{change.field_label}:</FieldLabel>{' '}
        <DiffSpans spans={change.diff} field={change.field} />
      </ChangeLine>
    )
  }

  const oldValue = humanizeChangeLogValue(change.old, t, change.field)
  const newValue = humanizeChangeLogValue(change.new, t, change.field)
  const hasOld = oldValue != null && oldValue !== ''
  const hasNew = newValue != null && newValue !== ''

  if (hasOld || hasNew) {
    return (
      <ChangeLine>
        <FieldLabel>{change.field_label}:</FieldLabel>{' '}
        {hasOld ? <ValuePill $tone="old">{oldValue}</ValuePill> : null}
        {hasOld && hasNew ? <ArrowIcon /> : null}
        {hasNew ? <ValuePill $tone="new">{newValue}</ValuePill> : null}
      </ChangeLine>
    )
  }

  return (
    <ChangeLine>
      <FieldLabel>{change.field_label}:</FieldLabel>{' '}
      {humanizeChangeLogValue(change.display, t, change.field) ?? change.display}
    </ChangeLine>
  )
}

const ChangeLine = styled.div`
  line-height: 1.4;
`

const FieldLabel = styled.span`
  color: ${({ theme }) => theme.colors.iconGray};
  font-weight: 600;
`

const DiffText = styled.span`
  white-space: pre-wrap;
`

const valuePillToneStyles = ($tone: 'old' | 'new' | 'equal', theme: DefaultTheme) => {
  if ($tone === 'old') {
    return `
      color: ${theme.colors.error};
      background: #fdecea;
      text-decoration: line-through;
    `
  }
  if ($tone === 'new') {
    return `
      color: #1c7a45;
      background: #e8f9ee;
    `
  }
  return `
    color: #1a1d21;
    background: transparent;
    padding: 0;
    margin: 0;
  `
}

const ValuePill = styled.span<{ $tone: 'old' | 'new' | 'equal' }>`
  display: inline-block;
  border-radius: 0.2rem;
  padding: 0.02rem 0.25rem;
  margin-right: 0.1rem;
  ${({ $tone, theme }) => valuePillToneStyles($tone, theme)}
`

const ValuePillLink = styled(Link)<{ $tone: 'old' | 'new' }>`
  display: inline-block;
  border-radius: 0.2rem;
  padding: 0.02rem 0.25rem;
  margin-right: 0.1rem;
  ${({ $tone, theme }) => valuePillToneStyles($tone, theme)}

  &:hover {
    text-decoration: underline;
  }
`

const Arrow = styled.span`
  display: inline-flex;
  align-items: center;
  margin: 0 0.25rem;
  color: ${({ theme }) => theme.colors.iconGray};
  vertical-align: middle;

  svg {
    width: 0.6rem;
    height: 0.6rem;
  }
`
